import { useState, useContext, useEffect } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  TableContainer,
  Td,
  Spinner,
  Text,
  Center,
  Flex,
  Heading,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NoResults from "components/elements/NoResults";
import { LoansContext } from "providers/loans";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { CSVLink } from "react-csv";
import { generateLoanRows } from "utils/downloads";
import { FormatTableCell } from "pages/Loans/loans-table-cell";
import { LoanColumns } from "pages/Loans/loans-table-columns";
import { Pagination } from "components/elements/Pagination/pagination";

export function sortLoanFields(a: any, b: any) {
  return a.order - b.order;
}

const PAGE_SIZE = 10;

const DownloadLink = ({ loans, columns }: { loans: any[]; columns: any[] }) => {
  return loans.length > 0 ? (
    <CSVLink
      data={generateLoanRows(loans, columns)}
      filename={`loans_export_${new Date().getTime()}.csv`}
      style={{
        borderWidth: "1px",
        borderColor: "primary",
        color: "primary",
        paddingLeft: "1rem",
        paddingRight: "1rem",
        marginRight: "12px",
        borderRadius: "7px",
        display: "flex",
        alignItems: "center",
        verticalAlign: "middle",
        justifyContent: "center",
      }}
    >
      <FontAwesomeIcon icon={["fas", "download"]} />
      <Text color={"black"} marginLeft={"7px"}>
        Export
      </Text>
    </CSVLink>
  ) : null;
};

export function AnalyticsLoansTable({ columnsData, loans }: { columnsData: any[]; loans: any[] }) {
  const { loadingLoans, orderBy, setOrderBy } = useContext(LoansContext);
  const { user } = useTenant();
  const [columns, setColumns] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  useEffect(() => {
    if (user.type === "lender") {
      setColumns([...columnsData]);
    } else {
      setColumns([...columnsData.filter((field: any) => field.data_key !== "borrower")]);
    }
  }, [columnsData, user.type]);

  const totalPages = Math.ceil(loans.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleLoans = loans.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSelectAllRows = () => {
    const allIds: string[] = loans.map((obj: any) => obj.id);
    const allVisibleRowsSelected = selectedRows.length === allIds.length;
    const updatedSelection = allVisibleRowsSelected ? [] : allIds;
    setSelectAllChecked(!allVisibleRowsSelected);
    setSelectedRows(updatedSelection);
  };

  const handleRowSelect = (id: string) => {
    setSelectedRows(prevSelectedRows => {
      const updatedSelection = prevSelectedRows.includes(id)
        ? prevSelectedRows.filter(rowId => rowId !== id)
        : [...prevSelectedRows, id];
      setSelectAllChecked(updatedSelection.length === loans.length);
      return updatedSelection;
    });
  };

  const filteredColumns = columns.filter(
    column => column.show && column.label !== "Select" && column.label !== "Loan Name"
  );

  return (
    <Flex direction={"column"} gap={4}>
      <Flex justifyContent={"space-between"}>
        <Heading size="md" flexGrow={1}>
          Completed Loans
        </Heading>
        {selectedRows.length > 0 && (
          <Flex justifyContent="flex-end">
            <DownloadLink loans={loans} columns={filteredColumns} />
          </Flex>
        )}
      </Flex>

      <TableContainer>
        <Table>
          <Thead>
            <LoanColumns
              columns={columns}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              onRowSelect={handleSelectAllRows}
              selectAllCheck={selectAllChecked}
            />
          </Thead>
          {loans.length === 0 || loadingLoans ? (
            <Tbody>
              <Tr>
                <Td colSpan={columns.filter(col => col.show).length}>
                  {loadingLoans ? (
                    <Center>
                      <Spinner />
                    </Center>
                  ) : (
                    <NoResults loading={loadingLoans} message="No loans found." />
                  )}
                </Td>
              </Tr>
            </Tbody>
          ) : (
            <Tbody>
              {visibleLoans.map((loan: any) => (
                <Tr data-cy="loans-table-row" key={loan.id} cursor="pointer">
                  {columns
                    .filter(col => col.show)
                    .map(col => (
                      <FormatTableCell
                        loan={loan}
                        key={col.id}
                        type={col.type}
                        value={
                          col.data_key === "Select"
                            ? "checkbox"
                            : col.data_key
                            ? loan[col.data_key]
                            : loan.values.find((val: any) => val.loan_field.id === col.id)?.value
                                ?.field
                        }
                        onRowSelect={handleRowSelect}
                        selectedRows={selectedRows}
                      />
                    ))}
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>
      </TableContainer>

      <Pagination
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        totalCount={loans.length}
        pageSize={PAGE_SIZE}
      />
    </Flex>
  );
}
